"use client";

import { Avatar as BaseAvatar } from "@base-ui/react/avatar";
import type * as React from "react";

import { cn } from "@/lib/utils";

export type AvatarFallbackProps = React.ComponentProps<typeof BaseAvatar.Fallback> & {
  /**
   * Optional user info used to derive initials when `children` are not provided.
   */
  name?: string;
  email?: string;
  /**
   * Fallback text to use when initials can't be derived.
   * Defaults to "Ra" (Radium).
   */
  fallbackText?: string;
};

const getInitials = (user: { name?: string; email?: string } | null | undefined, fallbackText: string) => {
  const name = user?.name?.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
    if (parts.length === 1 && parts[0].length === 1) return (parts[0] + parts[0]).toUpperCase();
  }

  const email = user?.email?.trim();
  if (email) {
    const local = email.split("@")[0] ?? "";
    const parts = local.split(/[._-]/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
    if (parts.length === 1 && parts[0].length === 1) return (parts[0] + parts[0]).toUpperCase();
  }

  return fallbackText;
};

export const AvatarFallback = ({
  className,
  children,
  name,
  email,
  fallbackText = "Ra",
  ...props
}: AvatarFallbackProps) => {
  const content = children ?? getInitials({ name, email }, fallbackText);

  return (
    <BaseAvatar.Fallback
      data-slot="avatar-fallback"
      className={cn("bg-muted flex size-full items-center justify-center rounded-full font-extrabold", className)}
      {...props}
    >
      {content}
    </BaseAvatar.Fallback>
  );
};

export const AvatarImage = ({ className, ...props }: React.ComponentProps<typeof BaseAvatar.Image>) => {
  return <BaseAvatar.Image data-slot="avatar-image" className={cn("aspect-square size-full", className)} {...props} />;
};

type AvatarFallbackSlotProps = Omit<AvatarFallbackProps, "name" | "email" | "fallbackText">;

export type AvatarProps = React.ComponentProps<typeof BaseAvatar.Root> & {
  /**
   * Optional image source/alt.
   */
  src?: string;
  alt?: string;
  /**
   * Optional user info for computing fallback initials.
   */
  name?: string;
  email?: string;
  /**
   * Fallback text to use when initials can't be derived.
   * Defaults to "Ra" (Radium).
   */
  fallbackText?: string;
  /**
   * MUI-ish slot overrides (only `fallback` is supported for now).
   */
  slot?: {
    fallback?: React.ElementType<AvatarFallbackProps>;
  };
  /**
   * Slot props (only `fallback` is supported for now).
   */
  slotProps?: {
    fallback?: AvatarFallbackSlotProps;
  };
};

export const Avatar = ({
  className,
  children,
  src,
  alt,
  name,
  email,
  fallbackText = "Ra",
  slot,
  slotProps,
  ...props
}: AvatarProps) => {
  const hasCustomChildren = children != null;
  const resolvedName = name;
  const resolvedEmail = email;
  const resolvedSrc = src;

  const FallbackSlot = slot?.fallback ?? AvatarFallback;
  const fallbackSlotProps = slotProps?.fallback;

  return (
    <BaseAvatar.Root
      data-slot="avatar"
      className={cn("relative flex size-8 shrink-0 overflow-hidden rounded-full", className)}
      {...props}
    >
      {hasCustomChildren ? (
        children
      ) : (
        <>
          {resolvedSrc ? <AvatarImage src={resolvedSrc} alt={alt ?? resolvedName ?? ""} /> : null}
          <FallbackSlot {...fallbackSlotProps} name={resolvedName} email={resolvedEmail} fallbackText={fallbackText} />
        </>
      )}
    </BaseAvatar.Root>
  );
};

Avatar.displayName = "Avatar";
AvatarImage.displayName = "AvatarImage";
AvatarFallback.displayName = "AvatarFallback";
